import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, forkJoin } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from './api.service';

/**
 * RealtimeNotificationService
 * ----------------------------
 * Singleton (providedIn: 'root') — only ONE poll loop runs for the entire app.
 *
 * Design choices for performance:
 *  - Poll interval: 3 minutes (180 s).  Matches auto-reload of child pages, so
 *    we never fire extra HTTP requests on top of what the page already does.
 *  - Uses `forkJoin` so both API calls happen in parallel (one round trip cost).
 *  - Compares only IDs stored in a Set<string> — O(1) lookup, no deep diffing.
 *  - Sidebar subscribes via `async` pipe — zero manual unsubscribe needed.
 *  - Counts are capped at 99 to avoid large number rendering issues.
 *  - On first load we just seed the known-ID cache without raising notifications
 *    (avoids false positives on app start).
 */
@Injectable({ providedIn: 'root' })
export class RealtimeNotificationService implements OnDestroy {

  // --- Public streams ---
  readonly parkingExitCount$ = new BehaviorSubject<number>(0);
  readonly evExitCount$      = new BehaviorSubject<number>(0);

  // --- Internal state ---
  private knownParkingExitIds = new Set<string>();
  private knownEvExitIds      = new Set<string>();

  /** True after the very first successful fetch — prevents false positives on boot. */
  private parkingSeeded = false;
  private evSeeded      = false;

  private destroy$ = new Subject<void>();
  private intervalId: ReturnType<typeof setInterval> | null = null;

  /** How often to poll (ms). 3 minutes — matches the page auto-reload. */
  private readonly POLL_INTERVAL_MS = 3 * 60 * 1000;

  constructor(
    private apiService: ApiService,
    private cookieService: CookieService,
  ) {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.intervalId) clearInterval(this.intervalId);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Call from the sidebar menu item click to acknowledge / clear the badge. */
  clearParking(): void { this.parkingExitCount$.next(0); }
  clearEv():      void { this.evExitCount$.next(0); }

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------

  private startPolling(): void {
    // Immediately run once on startup to seed the cache.
    this.poll();

    this.intervalId = setInterval(() => this.poll(), this.POLL_INTERVAL_MS);
  }

  private poll(): void {
    const today = new Date().toISOString().split('T')[0];

    // Fetch parking and EV in parallel.
    // Use a dummy userId=0 (dashboard view) — adjust if you have user context here.
    const userId = Number(this.cookieService.get('user_id') || '0');

    forkJoin({
      parking: this.apiService.getTransactionParking(today, today, userId).pipe(
        catchError(() => of([]))
      ),
      ev: this.apiService.GetTransactionEV(today, today).pipe(
        catchError(() => of([]))
      ),
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe(({ parking, ev }) => {
      this.processParkingData(parking ?? []);
      this.processEvData(ev ?? []);
    });
  }

  // ---------------------------------------------------------------------------
  // Diffing helpers
  // ---------------------------------------------------------------------------

  private processParkingData(items: any[]): void {
    // Only records that have already exited (exitDate not null/empty/"0001...")
    const exitedIds = new Set<string>(
      items
        .filter(t => t.exitDate && !t.exitDate.startsWith('0001'))
        .map(t => String(t.id))
    );

    if (!this.parkingSeeded) {
      // First run — just seed the cache, no notification.
      this.knownParkingExitIds = exitedIds;
      this.parkingSeeded = true;
      return;
    }

    // Count IDs that are newly exited since last poll.
    let newCount = 0;
    exitedIds.forEach(id => {
      if (!this.knownParkingExitIds.has(id)) newCount++;
    });
    this.knownParkingExitIds = exitedIds;

    if (newCount > 0) {
      const prev = this.parkingExitCount$.value;
      this.parkingExitCount$.next(Math.min(prev + newCount, 99));
    }
  }

  private processEvData(items: any[]): void {
    // EV uses `exitAt` field.
    const exitedIds = new Set<string>(
      items
        .filter(t => t.exitAt && !String(t.exitAt).startsWith('0001'))
        .map(t => String(t.id))
    );

    if (!this.evSeeded) {
      this.knownEvExitIds = exitedIds;
      this.evSeeded = true;
      return;
    }

    let newCount = 0;
    exitedIds.forEach(id => {
      if (!this.knownEvExitIds.has(id)) newCount++;
    });
    this.knownEvExitIds = exitedIds;

    if (newCount > 0) {
      const prev = this.evExitCount$.value;
      this.evExitCount$.next(Math.min(prev + newCount, 99));
    }
  }
}
